import { Tag, baseApi } from './base'

import {
  AirtableBasesDiscovery,
  AirtableConfigResponse,
  AirtableInboundSyncStatus,
  AirtablePatientTableStatus,
  AirtableSetupVerificationResult,
  Establishment,
  ExportMedConnectPatientsToAirtableResult,
  UpdateAirtableConfigDto,
  UpdateEstablishmentDto,
} from '@/core/models/establishment.model'
import { Staff } from '@/core/models/staff.model'
import { SearchRequestOptions } from '@/core/types/request'
import { ApiKey } from '@/core/models/apiKey.model'

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminAccounts: builder.query<(Staff | ApiKey)[], void>({
      query: () => ({
        url: '/admin/accounts',
        method: 'GET',
      }),
    }),
    getAdminEstablishment: builder.query<Establishment, void>({
      query: () => '/admin/etablissement',
      providesTags: (_result, _errors, args) => [{ type: Tag.ESTABLISHMENT, id: _result?._id }],
    }),
    getAdminEstablishments: builder.query<Establishment[], void>({
      query: () => ({
        url: `/admin/etablissements`,
        method: 'GET',
      }),
    }),
    getAdminStaffs: builder.query<[Staff[], number], { filter: Record<string, any>; options?: SearchRequestOptions }>({
      query: ({ filter, options }) => ({
        url: '/admin/staffs',
        method: 'GET',
        params: { ...filter, ...options },
      }),
      providesTags: [{ type: Tag.STAFF, id: 'ADMIN:STAFFS' }],
    }),
    assignLicensesToStaff: builder.mutation<Staff, { staffId: string; licenses: string[] }>({
      query: ({ staffId, licenses }) => ({
        url: `/admin/staffs/${staffId}/licenses`,
        method: 'PATCH',
        body: { licenses },
      }),
    }),
    updateAdminEstablishment: builder.mutation<Establishment, UpdateEstablishmentDto & { establishmentId: string }>({
      query: ({ establishmentId, ...body }) => ({
        url: `/admin/etablissements/${establishmentId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _errors, args) => [{ type: Tag.ESTABLISHMENT, id: args.establishmentId }],
    }),
    getAirtableConfig: builder.query<AirtableConfigResponse, string>({
      query: (establishmentId) => `/admin/etablissements/${establishmentId}/airtable-config`,
      providesTags: (_result, _errors, establishmentId) => [{ type: Tag.ESTABLISHMENT, id: `AIRTABLE:${establishmentId}` }],
    }),
    updateAirtableConfig: builder.mutation<AirtableConfigResponse, UpdateAirtableConfigDto & { establishmentId: string }>({
      query: ({ establishmentId, ...body }) => ({
        url: `/admin/etablissements/${establishmentId}/airtable-config`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _errors, args) => [
        { type: Tag.ESTABLISHMENT, id: `AIRTABLE:${args.establishmentId}` },
        { type: Tag.ESTABLISHMENT, id: `AIRTABLE_PATIENTS:${args.establishmentId}` },
      ],
    }),
    discoverAirtableBases: builder.mutation<AirtableBasesDiscovery, { establishmentId: string; apiKey?: string }>({
      query: ({ establishmentId, apiKey }) => ({
        url: `/admin/etablissements/${establishmentId}/airtable-config/discover-bases`,
        method: 'POST',
        body: { apiKey },
      }),
    }),
    getAirtablePatientsTableStatus: builder.query<AirtablePatientTableStatus, string>({
      query: (establishmentId) => `/admin/etablissements/${establishmentId}/airtable-config/patients-table`,
      providesTags: (_result, _errors, establishmentId) => [{ type: Tag.ESTABLISHMENT, id: `AIRTABLE_PATIENTS:${establishmentId}` }],
    }),
    initializeAirtablePatientsTable: builder.mutation<AirtablePatientTableStatus, string>({
      query: (establishmentId) => ({
        url: `/admin/etablissements/${establishmentId}/airtable-config/patients-table`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _errors, establishmentId) => [
        { type: Tag.ESTABLISHMENT, id: `AIRTABLE:${establishmentId}` },
        { type: Tag.ESTABLISHMENT, id: `AIRTABLE_PATIENTS:${establishmentId}` },
      ],
    }),
    exportMedConnectPatientsToAirtable: builder.mutation<ExportMedConnectPatientsToAirtableResult, string>({
      query: (establishmentId) => ({
        url: `/admin/etablissements/${establishmentId}/airtable-config/patients-table/export-medconnect`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _errors, establishmentId) => [{ type: Tag.ESTABLISHMENT, id: `AIRTABLE_INBOUND:${establishmentId}` }],
    }),
    getAirtableInboundSyncStatus: builder.query<AirtableInboundSyncStatus, string>({
      query: (establishmentId) => `/admin/etablissements/${establishmentId}/airtable-config/inbound-sync`,
      providesTags: (_result, _errors, establishmentId) => [{ type: Tag.ESTABLISHMENT, id: `AIRTABLE_INBOUND:${establishmentId}` }],
    }),
    enableAirtableInboundSync: builder.mutation<AirtableInboundSyncStatus, string>({
      query: (establishmentId) => ({
        url: `/admin/etablissements/${establishmentId}/airtable-config/inbound-sync`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _errors, establishmentId) => [
        { type: Tag.ESTABLISHMENT, id: `AIRTABLE:${establishmentId}` },
        { type: Tag.ESTABLISHMENT, id: `AIRTABLE_INBOUND:${establishmentId}` },
      ],
    }),
    verifyAirtableInboundSync: builder.mutation<AirtableSetupVerificationResult, string>({
      query: (establishmentId) => ({
        url: `/admin/etablissements/${establishmentId}/airtable-config/inbound-sync/verify`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _errors, establishmentId) => [{ type: Tag.ESTABLISHMENT, id: `AIRTABLE_INBOUND:${establishmentId}` }],
    }),
    deleteAdminStaff: builder.mutation<Staff, { staffId: string; associatedData?: string[] }>({
      query: ({ staffId, associatedData }) => ({
        url: `/admin/staffs/${staffId}`,
        method: 'DELETE',
        params: associatedData ? { associatedData } : {},
      }),
      invalidatesTags: [{ type: Tag.STAFF, id: 'ADMIN:STAFFS' }],
    }),

    updateEstablismentLogo: builder.mutation<Establishment, { establishmentId: string; file: File }>({
      query: ({ establishmentId, file }) => {
        const body = new FormData()
        body.append('file', file)
        return {
          url: `/admin/etablissements/${establishmentId}/logo`,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: (_result, _errors, args) => [{ type: Tag.ESTABLISHMENT, id: args.establishmentId }],
    }),
  }),
  overrideExisting: false,
})
